import React, {useMemo} from "react";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {IconButton} from "../../../features/management-v2/import/inputs";
import {Contact} from "../typedefs";
import {useEditContact} from "../hooks/useEditContact";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useGetContact} from "../hooks/useGetContact";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import {Layout} from "../Layout";
import {GolemText} from "../../../lib/golem/GolemField";
import {GolemPhoneInput} from "../../../lib/golem/GolemPhoneInput";
import {useGolemErrors} from "../../../lib/golem/useGolemErrors";
import {FieldError} from "../../../lib/golem/FieldError";
import {isAxiosError} from "axios";
import {usePermissions} from "../../../context/modulePermissions";

export function ContactEdit() {
    const {id} = useParams();
    const {data, isLoading, error} = useGetContact(id);

    const content = useMemo(() => {
        if (data) return <ContactEditForm contact={data}/>;
        if (error) return <p className="text-red">Error loading contact</p>;
        if (isLoading) return <div className="flex justify-center p-8"><LoadingSpinner/></div>;
    }, [data, isLoading, error]);

    return (
        <Layout menuCurrent={"contacts"}>
            <div className="bg-white shadow-md p-4 flex flex-col gap-4">
                {content}
            </div>
        </Layout>
    );
}

function ContactEditForm({contact}: {contact: Contact}) {
    const [searchParams] = useSearchParams();
    const redirect = searchParams.get("redirect");
    const navigate = useNavigate();

    const {
        firstName, setFirstName,
        lastName, setLastName,
        phoneMobileWork, setPhoneMobileWork,
        phoneMobilePersonal, setPhoneMobilePersonal,
        phoneHome, setPhoneHome,
        emailWork, setEmailWork,
        emailPersonal, setEmailPersonal,
        isLoading,
        submit,
    } = useEditContact(contact, redirect);

    const {hasFullAccess} = usePermissions();
    const canEdit = hasFullAccess("contacts");
    const golemErrors = useGolemErrors();
    const isUserLinked = !!contact.userID;

    const handleSubmit = async () => {
        golemErrors.clearErrors();
        try {
            await submit();
        } catch (e) {
            if (isAxiosError(e) && e.response?.status === 403) {
                golemErrors.setFromAxiosError({response: {data: {errors: {_: ["This contact is linked to a user account and cannot be edited here"]}}}});
            } else {
                golemErrors.setFromAxiosError(e);
            }
        }
    };

    return (
        <>
            <div className="flex items-center gap-3">
                {redirect && (
                    <button onClick={() => navigate(redirect)} className="text-grey hover:text-dark-grey">
                        <ArrowBackIcon fontSize="small"/>
                    </button>
                )}
                <h1 className="text-lg font-semibold">Update Contact ({firstName} {lastName})</h1>
            </div>
            {isUserLinked && (
                <p className="text-dark-orange text-sm">This contact is linked to a user account. Name, email, and phone numbers are managed automatically.</p>
            )}
            <div className="grid grid-cols-2 gap-4">
                <GolemText label="First Name" value={firstName} onChange={setFirstName} errors={golemErrors} field="firstName" required disabled={isUserLinked || !canEdit}/>
                <GolemText label="Last Name" value={lastName} onChange={setLastName} errors={golemErrors} field="lastName" required disabled={isUserLinked || !canEdit}/>
                <GolemPhoneInput label="Work Mobile Phone" initialValue={phoneMobileWork} onChange={setPhoneMobileWork} errors={golemErrors} field="phoneMobileWork" disabled={isUserLinked || !canEdit}/>
                <GolemPhoneInput label="Personal Mobile Phone" initialValue={phoneMobilePersonal} onChange={setPhoneMobilePersonal} errors={golemErrors} field="phoneMobilePersonal" disabled={isUserLinked || !canEdit}/>
                <GolemPhoneInput label="Home Phone" initialValue={phoneHome} onChange={setPhoneHome} errors={golemErrors} field="phoneHome" disabled={isUserLinked || !canEdit}/>
                <GolemText label="Work Email" value={emailWork} onChange={setEmailWork} errors={golemErrors} field="emailWork" disabled={isUserLinked || !canEdit}/>
                <GolemText label="Personal Email" value={emailPersonal} onChange={setEmailPersonal} errors={golemErrors} field="emailPersonal" disabled={isUserLinked || !canEdit}/>
            </div>
            <FieldError error={golemErrors.fieldError("_")}/>
            {!isUserLinked && canEdit && (
                <IconButton text="Save" loading={isLoading} onClick={handleSubmit} icon={<SaveIcon/>} color="green"/>
            )}
        </>
    );
}
